import { parsePhoneNumber, isValidPhoneNumber, CountryCode } from 'libphonenumber-js';

/**
 * Valide et formate un numéro de téléphone au format E.164
 * @param phoneNumber - Le numéro à valider (peut être dans différents formats)
 * @param defaultCountry - Le pays par défaut (France par défaut)
 * @returns Le numéro formaté en E.164 ou null si invalide
 */
export function validateAndFormatPhone(
  phoneNumber: string,
  defaultCountry: CountryCode = 'FR'
): { isValid: boolean; formatted: string | null; error?: string } {
  if (!phoneNumber || phoneNumber.trim() === '') {
    return { isValid: false, formatted: null, error: 'Le numéro de téléphone est requis' };
  }

  const cleaned = phoneNumber.trim();

  try {
    // Vérifier si c'est déjà au format E.164
    if (cleaned.startsWith('+')) {
      if (isValidPhoneNumber(cleaned)) {
        const parsed = parsePhoneNumber(cleaned);
        return { isValid: true, formatted: parsed.number as string };
      }
      return { isValid: false, formatted: null, error: 'Numéro invalide' };
    }

    // Essayer de parser avec le pays par défaut
    if (isValidPhoneNumber(cleaned, defaultCountry)) {
      const parsed = parsePhoneNumber(cleaned, defaultCountry);
      return { isValid: true, formatted: parsed.number as string };
    }

    // Si ça commence par 0, essayer de remplacer par +33
    if (cleaned.startsWith('0') && defaultCountry === 'FR') {
      const withPrefix = '+33' + cleaned.substring(1).replace(/\s/g, '');
      if (isValidPhoneNumber(withPrefix)) {
        const parsed = parsePhoneNumber(withPrefix);
        return { isValid: true, formatted: parsed.number as string };
      }
    }

    return { isValid: false, formatted: null, error: 'Format invalide. Utilisez le format +33612345678' };
  } catch (_error) {
    return { isValid: false, formatted: null, error: 'Format invalide. Utilisez le format +33612345678' };
  }
}

/**
 * Formate un numéro pour l'affichage (avec espaces)
 * @param phoneNumber - Le numéro au format E.164
 * @returns Le numéro formaté pour l'affichage
 */
export function formatPhoneForDisplay(phoneNumber: string): string {
  try {
    const parsed = parsePhoneNumber(phoneNumber);
    return parsed.formatInternational();
  } catch {
    return phoneNumber;
  }
}

/**
 * Exemples de formats acceptés pour la France:
 * - +33612345678 (E.164)
 * - +33 6 12 34 56 78 (E.164 avec espaces)
 * - 0612345678 (format national)
 * - 06 12 34 56 78 (format national avec espaces)
 */
